// The one place that knows how the server reports failure, so no screen has to
// guess whether it is holding a body, a message or an object.
export class ApiError extends Error {
  constructor(status, detail) {
    super(`${status}: ${detail}`);
    this.status = status;
    this.detail = detail;
  }
}

/**
 * How long to wait before deciding a request is never coming.
 *
 * A fetch that hangs is the worst failure this app has, because it is silent:
 * the promise never settles, so no catch runs and no error is ever shown. The
 * screen just says "Loading…" for ever and the app looks broken rather than
 * disconnected — which is exactly what a phone on a dropping connection did.
 * The server can log a perfectly good 200 for a response the phone never
 * finished receiving, so "it answered" is not the same as "it arrived".
 *
 * Long enough for a bad pub connection, short enough to be a wait rather than
 * a dead end. Writes carry an event_id and are deduplicated by the log, so a
 * timeout that fires on a request the server did process costs nothing.
 */
const TIMEOUT_MS = 12_000;

/**
 * What to say when the body is not JSON, so the answer is not ours.
 *
 * The status is the only thing we know about whoever DID answer, and the
 * three cases want different words because they are somebody else's problem
 * in different ways.
 *
 * 2xx — a captive portal serving its sign-in page in our place. The user can
 * fix it, so say how. The whole successful range, not `=== 200`, because that
 * is what `res.ok` covered before this was lifted out of `request`.
 *
 * 403 — FORBIDDEN, from something that is not us: GitHub issue #91, a work
 * laptop behind Zscaler, whose own interstitial URL (`/zia-session/`) was in
 * the address bar of the photo on the issue. This one used to fall into the
 * bucket below and say "the server sent an error page", which reads as
 * Cueforge being broken -- and that is exactly what it was reported as
 * ("is this error with the app or being blocked by web security"). We can be
 * more definite than that: every 403 this app raises is an `HTTPException`,
 * which FastAPI renders as JSON, so a 403 whose body is HTML did not come
 * from the app at all. Something in between refused to pass the request on.
 *
 * Anything else — 502 and friends, which usually IS our reverse proxy having
 * a bad day. Not the user's to fix and not their fault, so it gets the status
 * and no advice. Blaming their network here would be the same misattribution
 * in the opposite direction.
 */
function notOurs(status) {
  if (status >= 200 && status < 300) {
    return 'something on this network answered instead of Cueforge. '
      + 'If you are on pub wifi, open any web page to finish signing in '
      + 'to it, or switch to mobile data.';
  }
  if (status === 403) {
    return 'something on this network blocked the request before it reached '
      + 'Cueforge (403). A work or school web filter is the usual cause — '
      + 'try mobile data or a different network.';
  }
  return `the server sent an error page instead of an answer (${status})`;
}

async function request(method, path, body, { timeout = TIMEOUT_MS } = {}) {
  const init = { method, headers: {} };
  if (body !== undefined) {
    init.headers['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const controller = new AbortController();
  init.signal = controller.signal;
  const timer = setTimeout(() => controller.abort(), timeout);
  let res, text;
  try {
    res = await fetch(path, init);
    // Inside the timeout as well: headers can arrive and the body still stall.
    text = await res.text();
  } catch (e) {
    throw new ApiError(0, e.name === 'AbortError'
      ? `no answer after ${Math.round(timeout / 1000)} seconds`
      : (e.message || 'no connection'));
  } finally {
    clearTimeout(timer);
  }
  // GitHub issue #76: this parse used to be bare, and it is the ONE line here
  // outside a catch. A phone on a pub wifi got `<!--# DKBN...` -- a captive
  // portal answering instead of us -- and the sign-in screen showed
  // JSON.parse's own words: "Unexpected token '<', "<!--# DKBN"... is not
  // valid JSON". Everything below already copes with a body that is not what
  // we expected, including falling back to the raw text; none of it could run,
  // because the throw happened first.
  //
  // A SyntaxError also carries no `detail`, and every screen renders
  // `e.detail || e.message`, so the parser's message is what the user reads.
  let parsed = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiError(res.status, notOurs(res.status));
    }
  }
  if (!res.ok) {
    const detail = parsed && parsed.detail ? parsed.detail : text;
    throw new ApiError(res.status, typeof detail === 'string'
      ? detail : JSON.stringify(detail));
  }
  return parsed;
}

/**
 * Anything that is not a GET may have changed what a read would return.
 *
 * Caches subscribe here rather than each write site remembering to clear them
 * — a list of sites to keep in step is a list somebody will forget to add to.
 */
const watchers = [];
export function onMutation(fn) { watchers.push(fn); }

export const get = (path) => request('GET', path);
const mutate = async (method, path, body) => {
  try {
    return await request(method, path, body);
  } finally {
    watchers.forEach((fn) => fn());
  }
};

export const post = (path, body) => mutate('POST', path, body);
export const put = (path, body) => mutate('PUT', path, body);
export const del = (path) => mutate('DELETE', path);

// WebAuthn speaks ArrayBuffers; JSON does not. Every challenge and credential
// id crosses that boundary on the way in. There is deliberately no encoder for
// the way out: PublicKeyCredential.toJSON() already produces exactly the shape
// py_webauthn parses, and hand-rolling it invites drift from the spec.
export const b64uToBytes = (s) => Uint8Array.from(
  atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));
